import React, { useMemo, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Drawer, Input, List, Space, Tag, Typography } from 'antd';

const { Text, Paragraph } = Typography;

// Klickly-internal acronym glossary. Sourced from actual terms that appear
// in the admin UI / our codebase — keep this list honest (no invented terms).
// Add new entries as feature surfaces introduce new jargon.
const GLOSSARY = [
    {
        term: 'KCP',
        expansion: 'Klickly Consumer Platform',
        description:
            'Admin module at /kcp — publisher accounts, advertise generators/strategies, rewards, Giftly, upsells.'
    },
    {
        term: 'Giftly',
        expansion: null,
        description:
            'Klickly marketplace product. Public: https://giftly.klickly.com and https://giftly.klickly.com/marketplace. Admin surface at /kcp/giftly (Customers, GS Explorer, Invite codes, Tasks).'
    },
    {
        term: 'DSP',
        expansion: 'Demand-Side Platform',
        description:
            'Programmatic ad-buying platform (Basis). Klickly submits creatives + bid parameters through the DSP. See the DSP tab on a SingleAccount or the DSP Settings / DSP Creatives pages under /system.'
    },
    {
        term: 'GAB',
        expansion: 'Google Authorized Buyers',
        description: 'Google real-time-bidding marketplace. Internal debugger at /system/gab-debugger (non-prod only).'
    },
    {
        term: 'AWA',
        expansion: 'Automatic Widget Appearance',
        description:
            'Custom image ad units managed per account. Surfaced as the "AWA · Creative units" tab on a SingleAccount; debug explorer at /brands-v2/awa-explorer (non-prod only).'
    },
    {
        term: 'CCPA',
        expansion: 'California Consumer Privacy Act',
        description:
            'Customer data-privacy requests (view / erase a customer across Klickly). Admin page at /brands-v2/privacy.'
    },
    {
        term: 'CCC / NCC',
        expansion: 'Current-Customer Commission / New-Customer Commission',
        description:
            'Two commission rates Klickly can take — one for a brand (CCC, returning customers) and another for new ones (NCC). Configure at SingleAccount → Settings → Commissions.'
    },
    {
        term: 'GMV',
        expansion: 'Gross Merchandise Value',
        description:
            'Total dollar value of orders. Used as the denominator in commission caps and in the attribution-window scaling levers.'
    },
    {
        term: 'AOV',
        expansion: 'Average Order Value',
        description: 'Revenue divided by number of orders.'
    },
    {
        term: 'ROAS',
        expansion: 'Return On Ad Spend',
        description: 'Revenue ÷ ad spend. Shown in the Brief tab and in scheduled performance reports.'
    },
    {
        term: 'CPA',
        expansion: 'Cost Per Acquisition',
        description: 'Ad spend divided by number of new orders attributed.'
    },
    {
        term: 'CPM',
        expansion: 'Cost Per Mille (thousand impressions)',
        description:
            'DSP bid parameter. Multiplier and optional fixed-CPM override live at SingleAccount → Settings → DSP CPM adjustment.'
    },
    {
        term: 'MFA',
        expansion: 'Multi-Factor Authentication',
        description: 'Required second-factor login. Setup at /mfa/setup; verify at /mfa/verify.'
    },
    {
        term: 'UTM',
        expansion: 'Urchin Tracking Module',
        description:
            'URL parameters (utm_source, utm_campaign, utm_medium, …) that identify the attribution path for an order. Visible in the Order Insights drawer.'
    },
    {
        term: 'CLP',
        expansion: 'Custom Landing Page',
        description:
            'Brand-specific landing pages. Brief → Day-14 checks verifies they exist and are receiving expected traffic.'
    },
    {
        term: 'CSP',
        expansion: 'Curated Shopping Page',
        description: 'Giftly-specific product-set construct. See /kcp/giftly/gs-explorer.'
    }
];

const normalize = (s) => (s || '').toLowerCase();

const matches = (entry, q) => {
    if (!q) return true;
    const hay = normalize(`${entry.term} ${entry.expansion || ''} ${entry.description}`);
    return q
        .split(/\s+/)
        .filter(Boolean)
        .every((t) => hay.includes(t));
};

export const Glossary = ({ open, onClose }) => {
    const [query, setQuery] = useState('');
    const filtered = useMemo(() => GLOSSARY.filter((e) => matches(e, normalize(query.trim()))), [query]);

    return (
        <Drawer
            title='Glossary'
            open={open}
            onClose={onClose}
            width={480}
            destroyOnHidden
            extra={
                <Text type='secondary' style={{ fontSize: 12 }}>
                    {filtered.length} / {GLOSSARY.length}
                </Text>
            }
        >
            <Paragraph type='secondary' style={{ marginBottom: 16 }}>
                Internal acronyms used across the Klickly admin. Missing something? Add it to
                <Text code style={{ marginLeft: 4, marginRight: 4 }}>
                    src/components/Header/Glossary.js
                </Text>
                — one source of truth.
            </Paragraph>
            <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Search acronym or definition…'
                prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
                allowClear
                style={{ marginBottom: 16 }}
            />
            <List
                dataSource={filtered}
                locale={{ emptyText: 'No entries match your search.' }}
                renderItem={(entry) => (
                    <List.Item key={entry.term} style={{ alignItems: 'flex-start' }}>
                        <Space direction='vertical' size={4} style={{ width: '100%' }}>
                            <Space size={8} wrap>
                                <Tag color='blue' style={{ fontSize: 13, margin: 0 }}>
                                    {entry.term}
                                </Tag>
                                {entry.expansion ? <Text strong>{entry.expansion}</Text> : null}
                            </Space>
                            <Text type='secondary' style={{ fontSize: 13 }}>
                                {entry.description}
                            </Text>
                        </Space>
                    </List.Item>
                )}
            />
        </Drawer>
    );
};
